import { Fragment, useMemo, useState } from "react";
import { MoneyLabel } from "../../general/MoneyLabel.tsx";
import { SecondaryButton } from "../../general/SecondaryButton.tsx";
import { DEFAULT_CURRENCY } from "../../../types/constants.ts";
import { cn } from "../../../lib/utils.ts";
import {
    TaxTypeHeader,
    TaxTypeTotalRow,
    TaxYearHeader,
    TaxYearTotalRow,
} from "./components/TaxGroupHeader.tsx";
import { TaxPaymentRow } from "./components/TaxPaymentRow.tsx";
import { EmptyTaxHistory } from "./components/EmptyTaxHistory.tsx";
import { TaxPayment } from "./types";
import { buildTaxTree, hasVisiblePayments, paymentCountLabel, totalOf } from "./utils";

interface Props {
    payments: TaxPayment[];
}

const columnHeaderClasses = "border-b border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-900";

export function TaxHistoryView({ payments }: Props) {
    const years = useMemo(() => buildTaxTree(payments), [payments]);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const isEmpty = payments.length === 0;
    const isExpanded = (key: string) => !collapsed.has(key);
    const allCollapsed = years.every((year) => collapsed.has(year.key));
    const showPaymentColumnHeaders = hasVisiblePayments(years, isExpanded);

    const toggle = (key: string) => {
        setCollapsed((current) => {
            const next = new Set(current);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        setCollapsed(allCollapsed ? new Set() : new Set(years.map((year) => year.key)));
    };

    return (
        <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <div className="text-sm font-semibold text-gray-800">
                    Alle betalingen
                    <span className="ml-1 font-normal text-gray-600">· {paymentCountLabel(payments.length)}</span>
                </div>
                {years.length > 1 && (
                    <SecondaryButton
                        title={allCollapsed ? 'Alles uitklappen' : 'Alles inklappen'}
                        onClick={toggleAll}
                    />
                )}
            </div>

            <div className="w-full overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                    <tr>
                        <th className={cn(columnHeaderClasses, "text-left font-semibold")}>Belastingperiode</th>
                        <th className={cn(columnHeaderClasses, "text-left font-semibold")}>
                            {showPaymentColumnHeaders && 'Aanslagcode'}
                        </th>
                        <th className={cn(columnHeaderClasses, "text-right font-bold")}>
                            {showPaymentColumnHeaders && 'Betaald op'}
                        </th>
                        <th className={cn(columnHeaderClasses, "text-right font-bold")}>Betaald</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {isEmpty && <EmptyTaxHistory colSpan={4}/>}
                    {years.map((year) => (
                        <Fragment key={year.key}>
                            <TaxYearHeader
                                label={year.label}
                                total={year.total}
                                expanded={isExpanded(year.key)}
                                onToggle={() => toggle(year.key)}
                            />
                            {isExpanded(year.key) && year.taxTypes.map((taxType) => (
                                <Fragment key={taxType.key}>
                                    <TaxTypeHeader
                                        label={taxType.label}
                                        total={taxType.total}
                                        expanded={isExpanded(taxType.key)}
                                        onToggle={() => toggle(taxType.key)}
                                    />
                                    {isExpanded(taxType.key) && taxType.payments.map((payment, index) => (
                                        <TaxPaymentRow key={`${taxType.key}-${index}`} payment={payment}/>
                                    ))}
                                    {isExpanded(taxType.key) && (
                                        <TaxTypeTotalRow label={taxType.label} total={taxType.total}/>
                                    )}
                                </Fragment>
                            ))}
                            {isExpanded(year.key) && <TaxYearTotalRow year={year.year} total={year.total}/>}
                        </Fragment>
                    ))}
                    </tbody>
                    {!isEmpty && (
                    <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-50">
                        <td colSpan={3} className="px-4 py-2.5 text-sm font-semibold text-gray-800">Totaal</td>
                        <td className="px-4 py-2.5 text-right text-sm text-gray-800 tabular-nums whitespace-nowrap [&_*]:font-semibold">
                            <MoneyLabel value={totalOf(payments)} showCurrency={true} currency={DEFAULT_CURRENCY}/>
                        </td>
                    </tr>
                    </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}
